/**
 * Intermediate interfaces and classes for logging and our logger.
 *
 * File: \src\Logger.ts
 * Created Date: Monday, July 16th 2018, 10:56:34
 * Author: Sascha Herrmann
 * -----
 * Last Modified: November 22nd 2024, 08:14:16
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2019 Liebherr-Hausgeräte GmbH
 */

import { App } from 'vue';

import { LogControllerApi, UploadLoggingUsingPOSTRequest } from '@/api/apis';
import {
  LoggableContent,
  LoggingConfig,
  LoggingConfigServerFlushEnum,
  LogEvent,
  LoggingConfigLogLevelEnum
} from '@/api/models';

import InternalError from './components/messages/handler/InternalError';
import { useApiStore } from './api/ApiStore';
import { useLoginStore } from './components/login/LoginStore';
import { useUserNotificationStore } from '@/components/messages/UserNotificationStore';
import { useI18nStore } from '@/I18nStore';

/**
 * This is needed to access "window" in Typescript.
 *
 * @export
 * @interface LogWindow
 * @extends {Window}
 */
export interface LogWindow extends Window {
  bus_log_level: string;
  bus_log_console: boolean;
}

declare let window: LogWindow;

/**
 * This the main class the components log to. For every class a new instance should be
 * retrieved using
 *
 * 'this.$log.prefix(<class name>::<method name>'
 *
 * VS Code helps by using the expandable template 'logger' which automatically fills in the logging
 * code. The method name must be set manually at this time though.
 *
 * It just pushes log events to the CubeLogger.
 *
 * @export
 * @class CoreLogger
 */
export class CoreLogger {
  /**
   * The prefix for the logger and its events.
   *
   * @private
   * @type {string}
   * @memberof CoreLogger
   */
  private prefix: string;

  /**
   * The "parent class".
   *
   * @private
   * @type {CubeLogger}
   * @memberof CoreLogger
   */
  private parent: CubeLogger;

  /**
   * Creates an instance of CoreLogger.
   *
   * @param {string} prefix The prefix for the events.
   * @param {CubeLogger} parent The "parent" which is the one single CubeLogger.
   *
   * @memberof CoreLogger
   */
  constructor(prefix: string, parent: CubeLogger) {
    this.prefix = prefix;
    this.parent = parent;
  }

  /**
   * Log an event in debug mode.
   *
   * @param {string} message The message to be logged.
   * @param {*} [payload] The payload if exists.
   *
   * @memberof CoreLogger
   */
  public debug(message: string, payload?: any): void {
    this.parent.log('debug', `${this.prefix}:${message}`, payload);
  }

  /**
   * Log an event in info mode.
   *
   * @param {string} message The message to be logged.
   * @param {*} [payload] The payload if exists.
   *
   * @memberof CoreLogger
   */
  public info(message: string, payload?: any): void {
    this.parent.log('info', `${this.prefix}:${message}`, payload);
  }

  /**
   * Log an event in error mode.
   *
   * @param {string} message The message to be logged.
   * @param {*} [payload] The payload if exists.
   *
   * @memberof CoreLogger
   */
  public error(message: string, payload?: any): void {
    const i18nStore = useI18nStore();
    this.parent.log('error', `${this.prefix}:${message}`, payload);

    const notificationError: string = i18nStore.t('general.error');
    const notification: InternalError = new InternalError(notificationError, message, new Date());

    const userNotificationStore = useUserNotificationStore();
    userNotificationStore.pushUserNotification(notification);
  }

  /**
   * Log an event in warn mode.
   *
   * @param {string} message The message to be logged.
   * @param {*} [payload] The payload if exists.
   *
   * @memberof CoreLogger
   */
  public warn(message: string, payload?: any): void {
    this.parent.log('warn', `${this.prefix}:${message}`, payload);
  }
}

/**
 * Main class for the logging in the application.
 *
 * @export
 * @interface CubeLogger
 */
export class CubeLogger {
  public static $log: CubeLogger;

  /**
   * The config. Initially a dummy config that is valid until the "real" config is loaded.
   *
   * @private
   * @type {LoggingConfig}
   * @memberof CubeLogger
   */
  private config: LoggingConfig = {
    bufferSize: 300,
    logLevel: LoggingConfigLogLevelEnum.DEBUG,
    console: import.meta.env.NODE_ENV === 'development',
    serverFlush: LoggingConfigServerFlushEnum.OFF
  };

  /**
   * Allows us to check whether we're running on the initial config
   * or on the "configured" one. Initially false.
   *
   * @private
   * @type {boolean}
   * @memberof CubeLogger
   */
  private configured: boolean = false;

  /**
   * The buffer.
   *
   * @private
   * @type {LoggableEvent[]}
   * @memberof CubeLogger
   */
  private buffer: LogEvent[] = [];

  /**
   * The log levels supported here.
   *
   * @private
   * @type {string[]}
   * @memberof CubeLogger
   */
  private readonly logLevels: string[] = ['info', 'debug', 'warn', 'error'];

  /**
   * Calculated index for a quick check whether to flush or not.
   *
   * @private
   * @type {number}
   * @memberof CubeLogger
   */
  private autoFlushLevelIndex: number = 100;

  /**
   * The user name (upn) for the log upload.
   *
   * @private
   * @type {string}
   * @memberof CubeLogger
   */
  private upn: string;

  /**
   * Inits the logger.
   *
   * @param {LoggingConfig} config The logger config.
   *
   * @memberof CubeLogger
   */
  public init(config: LoggingConfig): void {
    console.log('Init of CubeLogger');

    // Can only be configured once!
    if (!this.configured) {
      this.config = config;

      this.configured = true;

      const loginStore = useLoginStore();
      this.upn = loginStore.getCurrentUserUpn;

      if (
        this.config.serverFlush === LoggingConfigServerFlushEnum.WARN ||
        this.config.serverFlush === LoggingConfigServerFlushEnum.ERROR
      ) {
        this.autoFlushLevelIndex = this.logLevels.indexOf(this.config.serverFlush);
      }
    }
  }

  /**
   * Creates an instance of the "real" CoreLogger.
   *
   * @param {string} prefix The prefix to use for the logger.
   *
   * @returns {CoreLogger} The instance configured with the prefix.
   *
   * @memberof CubeLogger
   */
  public prefix(prefix: string): CoreLogger {
    return new CoreLogger(prefix, this);
  }

  /**
   * Called by the CoreLogger to log an event.
   *
   * @param {string} level The level to log at (error, warn, debug, ...).
   * @param {string} event The event text to log.
   * @param {*} payload The payload to include.
   *
   * @memberof CubeLogger
   */
  public log(level: string, event: string, payload: any): void {
    // Make sure we have a payload
    let logPayload: string = '';

    if (payload) {
      if (typeof payload === 'string') {
        logPayload = 'message: ' + payload;
      } else if (payload instanceof Error) {
        logPayload = 'error:' + payload.stack || logPayload.toString();
      } else {
        // Test that we can stringify
        try {
          logPayload = 'object: ' + JSON.stringify(payload);
        } catch (err) {
          logPayload = '***Cannot stringify the payload***';
        }
      }
    }

    const logEvent: LogEvent = {
      level: level,
      event: event,
      payload: logPayload,
      timestamp: new Date().toISOString()
    };

    // If we print to the console ... then we do that
    // bus_log_console allows us to change that setting on the console.
    if (window.bus_log_console || this.config.console) {
      this.printToConsole(logEvent);
    }

    // We always push the event to the buffer
    this.pushToBuffer(logEvent);
  }

  /**
   * Adds the log event to the buffer. This method also checks the level
   * of the event as well as the state of the buffer.
   *
   * @private
   * @param {LogEvent} logEvent The event to log.
   *
   * @memberof CubeLogger
   */
  private pushToBuffer(logEvent: LogEvent): void {
    // Always push to our buffer ...
    this.buffer.push(logEvent);

    // ... and check the log handling:
    switch (this.config.serverFlush) {
      case LoggingConfigServerFlushEnum.BUFFER_SIZE: {
        if (this.buffer.length >= this.config.bufferSize) {
          // This also clears the existing buffer
          this.flushToServer();
        }

        return;
      }

      case LoggingConfigServerFlushEnum.WARN:
      case LoggingConfigServerFlushEnum.ERROR: {
        if (this.isAutoFlush(logEvent.level)) {
          // This also clears the existing buffer
          this.flushToServer();
        }

        return;
      }
      case LoggingConfigServerFlushEnum.OFF:
      default: {
        break;
      }
    }

    // If we're not flushing, then at least we're trimming
    this.trimBufferToSize();
  }

  /**
   * Prints an event to the console.
   *
   * @private
   * @param {LogEvent} logEvent The event to log.
   *
   * @memberof CubeLogger
   */
  private printToConsole(logEvent: LogEvent): void {
    // We have nothing to log to, so don't!
    if (!console || !console.log) {
      return;
    }

    // "bus_log_level" allows to set a different log level
    const logLevel = window.bus_log_level || this.config.logLevel;

    if (this.logLevels.indexOf(logEvent.level) < this.logLevels.indexOf(logLevel)) {
      return;
    }

    const args = [logEvent.level, logEvent.timestamp, logEvent.event];
    if (logEvent.payload) {
      args.push(logEvent.payload);
    }

    try {
      if (console[logEvent.level] && console[logEvent.level].apply) {
        console[logEvent.level].apply(console, args);
      } else if (console.log && console.log.apply) {
        console.log.apply(console, args.toString() as any);
      }
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * Flushes the events to the server.
   *
   * @private
   * @memberof CubeLogger
   */
  private flushToServer(): void {
    // Copy the buffer
    const logBuffer: LogEvent[] = [...this.buffer];
    this.buffer = [];

    const logContent: LoggableContent = {
      events: logBuffer,
      meta: {
        upn: this.upn,
        navigator: navigator.userAgent
      }
    };

    const request: UploadLoggingUsingPOSTRequest = {
      loggableContent: logContent
    };

    const apiStore = useApiStore();
    const logControllerApi: LogControllerApi = apiStore.getLogControllerApi;

    logControllerApi.uploadLoggingUsingPOST(request).catch(error => {
      console.error(error);
    });
  }

  /**
   * This trims our buffer to make sure the buffer still keeps its buffer size.
   *
   * @private
   *
   * @memberof CubeLogger
   */
  private trimBufferToSize(): void {
    while (this.buffer.length > this.config.bufferSize) {
      this.buffer.shift();
    }
  }

  /**
   * Checks whether the passed-in level would warrant a server flush.
   *
   * @private
   * @param {string} level The log level.
   * @returns {boolean} if the level would trigger a flush.
   *
   * @memberof CubeLogger
   */
  private isAutoFlush(level: string): boolean {
    return this.logLevels.indexOf(level) >= this.autoFlushLevelIndex;
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $log: CubeLogger;
  }
  interface ComponentCustomOptions {
    log?: CubeLogger;
  }
}

export default {
  install: (app: App): void => {
    const logger = new CubeLogger();
    app.config.globalProperties.$log = logger;
    CubeLogger.$log = logger;
  }
};
